import { useState, useEffect } from 'react';
import Layout from '../components/layout/Layout';
import { PurchasesTab, SalesTab } from '../components/services/orders';
import { useAuth } from '../hooks/useAuth';
import { getPurchasesCount, getSalesCount, getSalesAnalytics } from '../services/orderService';

/**
 * OrdersPage - Página de gestión de órdenes de servicios
 * Tabs: Compras (como cliente) y Ventas (como vendedor)
 */
const OrdersPage = () => {
  const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('purchases'); // purchases | sales
  const [purchasesCount, setPurchasesCount] = useState(0);
  const [salesCount, setSalesCount] = useState(0);
  const [completedCount, setCompletedCount] = useState(0);
  const [totalEarned, setTotalEarned] = useState(0);
  const [analyticsCurrency, setAnalyticsCurrency] = useState('ARS');

  useEffect(() => {
    if (!currentUser?.uid) return;
    const loadCounts = async () => {
      const [pRes, sRes, aRes] = await Promise.all([
        getPurchasesCount(currentUser.uid),
        getSalesCount(currentUser.uid),
        getSalesAnalytics(currentUser.uid)
      ]);
      if (pRes.success && pRes.count != null) setPurchasesCount(pRes.count);
      if (sRes.success && sRes.count != null) setSalesCount(sRes.count);
      if (aRes.success) {
        setCompletedCount(aRes.completedCount ?? 0);
        setTotalEarned(aRes.totalEarned ?? 0);
        if (aRes.currency) setAnalyticsCurrency(aRes.currency);
      }
    };
    loadCounts();
  }, [currentUser?.uid]);

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
        {/* Header */}
        <div className="bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700">
          <div className="container-custom">
            <div className="py-8">
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                Mis Órdenes
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Gestiona tus compras y ventas de servicios
              </p>
              {/* Analíticas de ventas */}
              <div className="flex flex-wrap gap-6 text-sm">
                <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                  <span className="font-medium text-gray-500 dark:text-gray-400">Servicios terminados:</span>
                  <span className="font-semibold text-gray-900 dark:text-white">{completedCount}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                  <span className="font-medium text-gray-500 dark:text-gray-400">Total ganado:</span>
                  <span className="font-semibold text-gray-900 dark:text-white">
                    {analyticsCurrency === 'ARS' ? '$ ' : ''}
                    {Number(totalEarned).toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                    {analyticsCurrency !== 'ARS' ? ` ${analyticsCurrency}` : ''}
                  </span>
                </div>
              </div>
            </div>

            {/* Tabs Navigation */}
            <div className="flex gap-8">
              <button
                onClick={() => setActiveTab('purchases')}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'purchases'
                    ? 'border-indigo-600 text-indigo-600 dark:border-indigo-400 dark:text-indigo-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                  </svg>
                  <span>Compras</span>
                  <span className="text-gray-500 dark:text-gray-400 font-normal">({purchasesCount})</span>
                </div>
              </button>

              <button
                onClick={() => setActiveTab('sales')}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'sales'
                    ? 'border-indigo-600 text-indigo-600 dark:border-indigo-400 dark:text-indigo-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>Ventas</span>
                  <span className="text-gray-500 dark:text-gray-400 font-normal">({salesCount})</span>
                </div>
              </button>
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="container-custom py-6">
          {activeTab === 'purchases' && <PurchasesTab />}
          {activeTab === 'sales' && <SalesTab />}
        </div>
      </div>
    </Layout>
  );
};

export default OrdersPage;
